
import { useState, useEffect } from 'react';
import { useJobs } from '../contexts/JobsContext';
import { Job } from '../types/job';

// Type for the analytics data we're generating
interface EmployerAnalytics {
  applicationsOverTime: {
    date: string;
    count: number;
  }[];
  jobPerformance: {
    title: string;
    applicants: number;
  }[];
  qualityOfCandidates: {
    score: string;
    count: number;
  }[];
  timeToHire: {
    month: string;
    days: number;
  }[];
  conversionRate: number;
  applicationCompletionRate: number;
  averageTimeToHire: number;
  totalActiveJobs: number;
}

export const useEmployerAnalytics = (
  employerId: string,
  candidates: any[]
): EmployerAnalytics => {
  const { getJobsByEmployer } = useJobs();
  const [analytics, setAnalytics] = useState<EmployerAnalytics>({
    applicationsOverTime: [],
    jobPerformance: [],
    qualityOfCandidates: [],
    timeToHire: [],
    conversionRate: 0,
    applicationCompletionRate: 0,
    averageTimeToHire: 0,
    totalActiveJobs: 0
  });

  useEffect(() => {
    if (!employerId) return;

    // Get all jobs for this employer
    const employerJobs = getJobsByEmployer(employerId);
    const activeJobs = employerJobs.filter(job => job.status === 'active');
    const jobIds = employerJobs.map(job => job.id);
    
    // Filter candidates by job IDs belonging to this employer
    const relevantCandidates = candidates.filter(
      candidate => jobIds.includes(candidate.jobId)
    );

    // Generate applications over time (last 7 days)
    const last7Days = generateLast7Days();
    const applicationsMap = new Map<string, number>();
    
    last7Days.forEach(day => {
      applicationsMap.set(day, 0);
    });
    
    relevantCandidates.forEach(candidate => {
      const date = getDateFromRelative(candidate.appliedDate);
      if (applicationsMap.has(date)) {
        applicationsMap.set(date, applicationsMap.get(date)! + 1);
      }
    });
    
    const applicationsOverTime = Array.from(applicationsMap).map(([date, count]) => ({
      date,
      count
    }));
    
    // Calculate job performance
    const jobPerformance = employerJobs
      .map(job => {
        const applicants = relevantCandidates.filter(c => c.jobId === job.id).length;
        return {
          title: job.title.length > 15 ? job.title.substring(0, 15) + '...' : job.title,
          applicants
        };
      })
      .sort((a, b) => b.applicants - a.applicants)
      .slice(0, 5); // Top 5 jobs
    
    // Calculate candidate quality distribution
    const scoreRanges = {
      '90-100': 0,
      '80-89': 0,
      '70-79': 0,
      '60-69': 0,
      'Below 60': 0
    };
    
    relevantCandidates.forEach(candidate => {
      const score = candidate.assessmentScore;
      if (score >= 90) scoreRanges['90-100']++;
      else if (score >= 80) scoreRanges['80-89']++;
      else if (score >= 70) scoreRanges['70-79']++;
      else if (score >= 60) scoreRanges['60-69']++;
      else scoreRanges['Below 60']++;
    });
    
    const qualityOfCandidates = Object.entries(scoreRanges).map(([score, count]) => ({
      score,
      count
    }));
    
    // Calculate time to hire (mock data)
    const timeToHire = [
      { month: 'Jan', days: 25 },
      { month: 'Feb', days: 22 },
      { month: 'Mar', days: 21 },
      { month: 'Apr', days: 20 },
      { month: 'May', days: 19 },
      { month: 'Jun', days: 18 }
    ];
    
    // Calculate other metrics
    const shortlistedCount = relevantCandidates.filter(c => c.status === 'shortlisted').length;
    const conversionRate = relevantCandidates.length > 0 
      ? Math.round((shortlistedCount / relevantCandidates.length) * 100) 
      : 0;
    
    const applicationCompletionRate = Math.round(Math.random() * 20 + 70); // Mock data (70-90%)
    const averageTimeToHire = Math.round(
      timeToHire.reduce((acc, curr) => acc + curr.days, 0) / timeToHire.length
    );
    
    setAnalytics({
      applicationsOverTime,
      jobPerformance,
      qualityOfCandidates,
      timeToHire,
      conversionRate,
      applicationCompletionRate,
      averageTimeToHire,
      totalActiveJobs: activeJobs.length
    });
    
  }, [employerId, candidates, getJobsByEmployer]);
  
  return analytics;
};

// Helper function to generate the last 7 days as strings
function generateLast7Days(): string[] {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    days.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return days;
}

// Helper function to map relative dates to actual dates
function getDateFromRelative(relativeDate: string): string {
  const today = new Date();
  
  if (relativeDate === 'Today') {
    return today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else if (relativeDate.includes('day')) {
    const days = parseInt(relativeDate.split(' ')[0]);
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else if (relativeDate.includes('week')) {
    const weeks = parseInt(relativeDate.split(' ')[0]);
    const date = new Date();
    date.setDate(date.getDate() - (weeks * 7));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  
  // Default to today if we can't parse it
  return today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
